import polyglotI18nProvider from 'ra-i18n-polyglot'
import type { I18nProvider, TranslationMessages } from 'react-admin'

import type { BombeoCompletion, OperationKind, ProductClass, ScaleEventType } from '../data'
import type { ScaleState } from '../data/source/summarise'
import type { MarpolAnnex } from '../data/source/types'
import catalanMessages from './ra-language-catalan'
import spanishMessages from './ra-language-spanish'

export const availableLocales = [
  { locale: 'es', name: 'Castellano' },
  { locale: 'ca', name: 'Català' },
] as const
export const LOCALES = availableLocales.map(({ locale }) => locale)
export type Locale = (typeof LOCALES)[number]

export const isLocaleSupported = (locale: string): locale is Locale => {
  return LOCALES.includes(locale as Locale)
}

export const getTranslations = (locale: string): Translations => {
  if (!isLocaleSupported(locale)) {
    throw new Error(`Unsupported locale: ${locale}`)
  }
  switch (locale) {
    case 'es':
      return es
    case 'ca':
      return ca
  }
}

export interface Translations extends TranslationMessages {
  common: {
    back: string
  }
  pages: {
    /** The page behind a key (/#/hidden): the SÒSTRAT read. */
    hidden: {
      title: string
      sostrat: {
        title: string
        action: string
        /** On the action while a run is under way: takes `status`. */
        in_progress: string
        /** A read already in progress (409): takes `since`. */
        busy: string
        /** GitHub refused the read (502): takes `message`. */
        refused: string
        never: string
        /** Takes `trigger`. */
        last_run: string
        requested_at: string
        finished_at: string
        counts: string
        problems: string
        status: { REQUESTED: string; RUNNING: string; SUCCEEDED: string; FAILED: string }
        trigger: { MANUAL: string; SCHEDULE: string }
      }
    }
    scales: {
      /** Button label; takes the event `type`. */
      add_special: string
      no_events: string
      /** The Gantt chart of a scale's events. */
      timeline: {
        /** A MARPOL/BOMBEO's name on its circle and card: takes `event` and `number`. */
        marker: string
      }
      /** What SOSTRAT reports about a berth stay. */
      stay: {
        state: string
        fondeo_previo: string
        anterior: string
        alternativos: string
        sostrat_terminal: string
        yes: string
        no: string
        /** Takes `quantity` (tonnes). */
        tonnes: string
        read_as_kg: string
        /** Takes `quantity` and `unit`, as stated. */
        implausible: string
        window: string
      }
      /** The BOMBEO timing measures (see `bombeoMetrics`). */
      metrics: {
        title: string
        inicio: string
        rendimiento: string
        salida: string
        /** Takes `value`. */
        hours: string
        /** Takes `value`. */
        hours_per_day: string
      }
      /** The +Info dialog: what the scale page leaves out. */
      info: {
        button: string
        /** Takes `scaleId` and `year`. */
        title: string
      }
    }
  }
  components: {
    manual_scale: {
      label: string
      label_seen_in_source: string
    }
  }
  resources: {
    scales: {
      name: string
      fields: {
        scaleId: string
        year: string
        state: string
        finished: string
        currentMuelle: string
        currentStartTime: string
        eventMarkers: string
        bombeoCompletion: string
        vessel: string
        imo: string
        eslora: string
        bcm: string
        consignataria: string
        eta: string
        etd: string
        firstEta: string
        firstEtd: string
        terminals: string
      }
      filters: {
        q: string
        active: string
        finished: string
        all: string
      }
      actions: {
        back_to_list: string
      }
    }
    scale_events: {
      name: string
      title_create: string
      title_edit: string
      actions: {
        back_to_scale: string
      }
      fields: {
        scaleId: string
        type: string
        startTime: string
        endTime: string
        connectionPoint: string
        productClass: string
        product: string
        quantityT: string
        operationKind: string
        marpolAnnex: string
        marpolNoticeAt: string
      }
      helpers: {
        endTime: string
        prefilled: string
      }
      /** Keyed by `SourceError` code, lower-cased. */
      errors: {
        not_special: string
        not_found: string
        invalid_start: string
        invalid_end: string
        end_before_start: string
        scale_closed: string
        no_stays: string
        start_outside_stays: string
      }
    }
  }
  enums: {
    ScaleEventType: Record<ScaleEventType, string>
    ProductClass: Record<ProductClass, string>
    OperationKind: Record<OperationKind, string>
    MarpolAnnex: Record<MarpolAnnex, string>
    ScaleState: Record<ScaleState, string>
    BombeoCompletion: Record<BombeoCompletion, string>
  }
}

export type TranslationKey = RecursiveKeyOf<Translations>

type RecursiveKeyOf<TObj extends object> = {
  [TKey in keyof TObj & (string | number)]: TObj[TKey] extends object ? `${TKey}` | `${TKey}.${RecursiveKeyOf<TObj[TKey]>}` : `${TKey}`
}[keyof TObj & (string | number)]

export const translate = (key: TranslationKey, translations: Translations): string => {
  const segments = key.split('.')
  let current: unknown = translations
  for (const segment of segments) {
    if (typeof current !== 'object' || current === null) {
      return key
    }
    current = (current as Record<string, unknown>)[segment]
    if (current === undefined) {
      return key
    }
  }
  if (typeof current === 'string') {
    return current
  }
  return key
}

export const getTranslate = (locale: Locale): ((key: TranslationKey) => string) => {
  const translations = getTranslations(locale)
  return (key: TranslationKey) => translate(key, translations)
}

export const es: Translations = {
  ...spanishMessages,
  common: {
    back: 'Volver',
  },
  pages: {
    hidden: {
      title: 'Datos de SÒSTRAT',
      sostrat: {
        title: 'SÒSTRAT',
        action: 'Actualizar SÒSTRAT',
        in_progress: 'Leyendo SÒSTRAT: %{status}',
        busy: 'Ya hay una lectura de SÒSTRAT en curso, desde %{since}.',
        refused: 'GitHub no ha iniciado la lectura: %{message}',
        never: 'SÒSTRAT no se ha leído todavía.',
        last_run: 'Última lectura (%{trigger})',
        requested_at: 'Solicitada',
        finished_at: 'Terminada',
        counts: 'Escrito',
        problems: 'Problemas',
        status: { REQUESTED: 'Solicitada', RUNNING: 'En curso', SUCCEEDED: 'Correcta', FAILED: 'Fallida' },
        trigger: { MANUAL: 'manual', SCHEDULE: 'programada' },
      },
    },
    scales: {
      add_special: 'Añadir %{type}',
      no_events: 'Sin eventos',
      timeline: {
        marker: '%{event} #%{number}',
      },
      stay: {
        state: 'Estado',
        fondeo_previo: 'Fondeo previo',
        anterior: 'Anterior',
        alternativos: 'Muelles alternativos',
        sostrat_terminal: 'Terminal (SOSTRAT)',
        yes: 'Sí',
        no: 'No',
        tonnes: '%{quantity} t',
        read_as_kg: 'declarada en kg',
        implausible: 'cantidad dudosa: %{quantity} %{unit}',
        window: 'Ventana declarada',
      },
      metrics: {
        title: 'Métricas',
        inicio: 'Inicio',
        rendimiento: 'Rendimiento',
        salida: 'Salida',
        hours: '%{value} h',
        hours_per_day: '%{value} h/24h',
      },
      info: {
        button: '+Info',
        title: 'Escala %{scaleId} (%{year})',
      },
    },
  },
  components: {
    manual_scale: {
      label: 'Escala creada manualmente',
      label_seen_in_source: 'Escala creada manualmente y confirmada por el origen',
    },
  },
  resources: {
    scales: {
      name: 'Escala |||| Escalas',
      fields: {
        scaleId: 'Escala',
        year: 'Año',
        state: 'Estado',
        finished: 'Finalizada',
        currentMuelle: 'Muelle',
        currentStartTime: 'Inicio',
        eventMarkers: 'Eventos',
        bombeoCompletion: 'Completo',
        vessel: 'Buque',
        imo: 'IMO',
        eslora: 'Eslora',
        bcm: 'BCM',
        consignataria: 'Consignataria',
        eta: 'ETA',
        etd: 'ETD',
        firstEta: 'Primera ETA',
        firstEtd: 'Primera ETD',
        terminals: 'Empresa',
      },
      filters: {
        q: 'Buscar',
        active: 'Activas',
        finished: 'Finalizadas',
        all: 'Todas',
      },
      actions: {
        back_to_list: 'Volver a la lista',
      },
    },
    scale_events: {
      name: 'Evento |||| Eventos',
      title_create: 'Nuevo evento',
      title_edit: 'Editar evento',
      actions: {
        back_to_scale: 'Volver a la escala',
      },
      fields: {
        scaleId: 'Escala',
        type: 'Tipo',
        startTime: 'Inicio',
        endTime: 'Fin',
        connectionPoint: 'Código de conexión',
        productClass: 'Producto',
        product: 'Producto exacto',
        quantityT: 'Cantidad (t)',
        operationKind: 'Tipo de operación',
        marpolAnnex: 'Tipo MARPOL',
        marpolNoticeAt: 'Hora aviso servicio MARPOL',
      },
      helpers: {
        endTime: 'Vacío = en curso',
        prefilled: 'Precargado desde SOSTRAT: confírmalo con el CIDO',
      },
      errors: {
        not_special: 'Este evento no se puede editar',
        not_found: 'No se ha encontrado el evento',
        invalid_start: 'Inicio no válido',
        invalid_end: 'Fin no válido',
        end_before_start: 'El fin no puede ser anterior al inicio',
        scale_closed: 'La escala ha terminado: ya no se le pueden añadir eventos',
        no_stays: 'La escala aún no tiene ningún atraque: no se le pueden añadir eventos MARPOL ni BOMBEO',
        start_outside_stays: 'El inicio tiene que caer dentro de un atraque de la escala',
      },
    },
  },
  enums: {
    ScaleEventType: {
      PLACE: 'Ubicación',
      MARPOL: 'MARPOL',
      BOMBEO: 'BOMBEO',
    },
    ProductClass: {
      HYDROCARBON: 'Hidrocarburo',
      CHEMICAL: 'Producto químico',
    },
    OperationKind: {
      LOAD: 'Carga',
      UNLOAD: 'Descarga',
    },
    MarpolAnnex: {
      I: 'MARPOL I',
      V: 'MARPOL V',
    },
    ScaleState: {
      PLANNED: 'Planificado',
      IN_PROGRESS: 'En curso',
      CLOSED: 'Cerrado',
      FINISHED: 'Finalizado',
    },
    BombeoCompletion: {
      COMPLETE: 'Completo',
      INCOMPLETE: 'Incompleto',
      UNKNOWN: '—',
    },
  },
}

export const ca: Translations = {
  ...catalanMessages,
  common: {
    back: 'Tornar',
  },
  pages: {
    hidden: {
      title: 'Dades de SÒSTRAT',
      sostrat: {
        title: 'SÒSTRAT',
        action: 'Actualitzar SÒSTRAT',
        in_progress: 'Llegint SÒSTRAT: %{status}',
        busy: 'Ja hi ha una lectura de SÒSTRAT en curs, des de %{since}.',
        refused: 'GitHub no ha iniciat la lectura: %{message}',
        never: "SÒSTRAT encara no s'ha llegit.",
        last_run: 'Darrera lectura (%{trigger})',
        requested_at: 'Sol·licitada',
        finished_at: 'Acabada',
        counts: 'Escrit',
        problems: 'Problemes',
        status: { REQUESTED: 'Sol·licitada', RUNNING: 'En curs', SUCCEEDED: 'Correcta', FAILED: 'Fallida' },
        trigger: { MANUAL: 'manual', SCHEDULE: 'programada' },
      },
    },
    scales: {
      add_special: 'Afegir %{type}',
      no_events: 'Sense esdeveniments',
      timeline: {
        marker: '%{event} #%{number}',
      },
      stay: {
        state: 'Estat',
        fondeo_previo: 'Fondeig previ',
        anterior: 'Anterior',
        alternativos: 'Molls alternatius',
        sostrat_terminal: 'Terminal (SOSTRAT)',
        yes: 'Sí',
        no: 'No',
        tonnes: '%{quantity} t',
        read_as_kg: 'declarada en kg',
        implausible: 'quantitat dubtosa: %{quantity} %{unit}',
        window: 'Finestra declarada',
      },
      metrics: {
        title: 'Mètriques',
        inicio: 'Inici',
        rendimiento: 'Rendiment',
        salida: 'Sortida',
        hours: '%{value} h',
        hours_per_day: '%{value} h/24h',
      },
      info: {
        button: '+Info',
        title: 'Escala %{scaleId} (%{year})',
      },
    },
  },
  components: {
    manual_scale: {
      label: 'Escala creada manualment',
      label_seen_in_source: "Escala creada manualment i confirmada per l'origen",
    },
  },
  resources: {
    scales: {
      name: 'Escala |||| Escales',
      fields: {
        scaleId: 'Escala',
        year: 'Any',
        state: 'Estat',
        finished: 'Finalitzada',
        currentMuelle: 'Moll',
        currentStartTime: 'Inici',
        eventMarkers: 'Esdeveniments',
        bombeoCompletion: 'Complet',
        vessel: 'Vaixell',
        imo: 'IMO',
        eslora: 'Eslora',
        bcm: 'BCM',
        consignataria: 'Consignatària',
        eta: 'ETA',
        etd: 'ETD',
        firstEta: 'Primera ETA',
        firstEtd: 'Primera ETD',
        terminals: 'Empresa',
      },
      filters: {
        q: 'Cercar',
        active: 'Actives',
        finished: 'Finalitzades',
        all: 'Totes',
      },
      actions: {
        back_to_list: 'Tornar a la llista',
      },
    },
    scale_events: {
      name: 'Esdeveniment |||| Esdeveniments',
      title_create: 'Nou esdeveniment',
      title_edit: 'Editar esdeveniment',
      actions: {
        back_to_scale: "Tornar a l'escala",
      },
      fields: {
        scaleId: 'Escala',
        type: 'Tipus',
        startTime: 'Inici',
        endTime: 'Fi',
        connectionPoint: 'Codi de connexió',
        productClass: 'Producte',
        product: 'Producte exacte',
        quantityT: 'Quantitat (t)',
        operationKind: "Tipus d'operació",
        marpolAnnex: 'Tipus MARPOL',
        marpolNoticeAt: 'Hora avís servei MARPOL',
      },
      helpers: {
        endTime: 'Buit = en curs',
        prefilled: 'Precarregat des de SOSTRAT: confirma-ho amb el CIDO',
      },
      errors: {
        not_special: 'Aquest esdeveniment no es pot editar',
        not_found: "No s'ha trobat l'esdeveniment",
        invalid_start: 'Inici no vàlid',
        invalid_end: 'Fi no vàlida',
        end_before_start: "La fi no pot ser anterior a l'inici",
        scale_closed: "L'escala ha acabat: ja no s'hi poden afegir esdeveniments",
        no_stays: "L'escala encara no té cap atracament: no s'hi poden afegir esdeveniments MARPOL ni BOMBEO",
        start_outside_stays: "L'inici ha de caure dins d'un atracament de l'escala",
      },
    },
  },
  enums: {
    ScaleEventType: {
      PLACE: 'Ubicació',
      MARPOL: 'MARPOL',
      BOMBEO: 'BOMBEO',
    },
    ProductClass: {
      HYDROCARBON: 'Hidrocarbur',
      CHEMICAL: 'Producte químic',
    },
    OperationKind: {
      LOAD: 'Càrrega',
      UNLOAD: 'Descàrrega',
    },
    MarpolAnnex: {
      I: 'MARPOL I',
      V: 'MARPOL V',
    },
    ScaleState: {
      PLANNED: 'Planificat',
      IN_PROGRESS: 'En curs',
      CLOSED: 'Tancat',
      FINISHED: 'Finalitzat',
    },
    BombeoCompletion: {
      COMPLETE: 'Complet',
      INCOMPLETE: 'Incomplet',
      UNKNOWN: '—',
    },
  },
}

export const i18nProvider: I18nProvider = polyglotI18nProvider((locale: string) => getTranslations(locale), 'es', availableLocales)
